import { inject, Injectable } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRouteSnapshot, CanDeactivate, RouterStateSnapshot } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { Observable, of } from "rxjs";
import { first, switchMap } from "rxjs/operators";

import { AuthModel } from "@intocare/auth-data";
import { PageFilterQueryParam } from "@intocare/client-side-state";
import { ParentSaveOnExitComponent, SaveOnExitComponent } from "@intocare/interfaces";
import { DialogService, ImpactType } from "@intocare/shared/ui-design-system";

@Injectable()
export class SaveOnExit implements CanDeactivate<SaveOnExitComponent | undefined> {
	private dialogService = inject(DialogService);
	private auth = inject(AuthModel);
	private translateService = inject(TranslateService);
	private readonly dialog = inject(MatDialog, { optional: true });

	findSaveOnExitComponent(component: SaveOnExitComponent | ParentSaveOnExitComponent): SaveOnExitComponent | null {
		let exitComponent;

		if (component && Object.hasOwn(component, "saveOnExitComponent")) {
			exitComponent = (component as ParentSaveOnExitComponent).saveOnExitComponent;

			if (exitComponent && Object.hasOwn(exitComponent, "saveOnExitComponent")) {
				exitComponent = this.findSaveOnExitComponent(exitComponent);
			}
		}

		if (exitComponent) {
			return exitComponent;
		}

		return component && Object.hasOwn(component, "saveOnExitComponent") ? null : (component as SaveOnExitComponent);
	}

	canDeactivate(
		exitComponent: ParentSaveOnExitComponent | SaveOnExitComponent,
		currentRoute: ActivatedRouteSnapshot,
		currentState: RouterStateSnapshot,
		nextState: RouterStateSnapshot,
	): Observable<boolean> | Promise<boolean> | boolean {
		if (!exitComponent) {
			// The `exitComponent` could not be found because it's not rendered so we don't have to check anything
			return true;
		}

		if (nextState.url === "/error") {
			this.dialog?.closeAll();
			this.dialogService.closeAll();
			return true;
		}

		// We need to know if the exitComponent is a ParentSaveOnExitComponent. If saveOnExitComponent property exists but the value is undefined we still need to pass this check.
		const component = this.findSaveOnExitComponent(exitComponent);

		if (!component) {
			// The `exitComponent` could not be found because it's not rendered so we don't have to check anything
			return true;
		}

		// If navigation was triggered by the page filter having added the 'department' query parameter programmatically (based on the selected resident), we don't want the warning dialog to be triggered.
		if (
			currentState?.url
			&& nextState?.url
			&& currentState.url === nextState.url.split(`&${PageFilterQueryParam.NAME_DEPARTMENT}=`)[0]
			&& currentState.root.queryParamMap.get(PageFilterQueryParam.NAME_RESIDENT)
				=== nextState.root.queryParamMap.get(PageFilterQueryParam.NAME_RESIDENT)
			&& currentState.root.queryParamMap.get(PageFilterQueryParam.NAME_DEPARTMENT)
				!== nextState.root.queryParamMap.get(PageFilterQueryParam.NAME_DEPARTMENT)
		) {
			return true;
		}

		return this.auth.isLoggedIn$.pipe(
			switchMap((loggedIn) => {
				if (!loggedIn || !component.isDirty() || component.isSaved()) {
					return of(true);
				}

				return this.showWarning(component);
			}),
		);
	}

	showWarning(component: SaveOnExitComponent) {
		return this.dialogService
			.confirm({
				impactType: ImpactType.Warning,
				message: component.getExitMessage
					? component.getExitMessage()
					: this.translateService.instant("saveOnExit.unsavedChanges"),
			})
			.pipe(
				first(),
				switchMap((value) => {
					if (value) {
						if (component.discard) {
							const result = component.discard();

							if (result) {
								return result;
							}

							return of(true);
						}

						return of(true);
					} else {
						return of(false);
					}
				}),
			);
	}
}
